<template>
  <div>
    <list-header title="Service Sales" class="mb-4" :style="{position: 'relative'}">
      <icon-button
        :icon-class="'bi bi-three-dots'"
        @click="showGroupByModal"
        :btnStyle="{
          bottom: '12px',
          right: '17px',
          position: 'absolute',
          fontSize: '18px',
        }"
      />
    </list-header>

    <pull-to-refresh :refreshFunction="getServiceSales">
      <!-- Service Sale Found -->
      <div v-if="populatedServiceSales && populatedServiceSales.length > 0">  <!-- if populatedServiceSales is not undefined(init value) and is not empty array(API return data) -->
        <div class="style-content">

          <nav-tab
            :tabs="tabList"
            :selected-tab="selectedTabID"
            @selected-tab="selectedTabID = $event"
            cssClass="scss-tabs"
          >
            <div v-for="serviceSale in populatedServiceSales" :key="`sec-${groupBy === 'section' ? serviceSale.sectionID : serviceSale.categoryID}`">
              <tab :isSelected="selectedTabID === (groupBy === 'section' ? serviceSale.sectionID : serviceSale.categoryID)">
                <fieldset id="group1">
                  <div
                    v-for="data in serviceSale.serviceSales"
                    :key="`srv-${data.id}`"
                    @click="viewItemDetail(data)"
                  >
                    <div
                      class="scss-round-container d-flex flex-row mb-2 scss-click-effect"
                    >
                      <expandable-list
                        class="flex-grow-1"
                        :border="data.promotion">
                        <expandable-list-header
                          v-if="data.promotion"
                          :content="getPromotionHeaderAlt(data)"/>
                        <expandable-list-item
                          :imgUrl="data.imageURL"
                          :name="data.name"
                          :processingTime="formatProcessingTime(data.type, data.processingTime)"
                          :description="data.description"
                          :originalPrice="data.originalPrice"
                          :salePrice="data.salePrice"
                          :clickable="false">
                          <div class="d-flex flex-row align-items-center me-2">
                            <icon-container
                              iconClass="bi-cart-fill"
                              :cssClass="'style-border'"
                              @click.stop="addToCart(data.id)"/>
                          </div>
                        </expandable-list-item>
                      </expandable-list>
                    </div>
                  </div>
                </fieldset>
              </tab>
            </div>
            <div class="style-action-container fixed-bottom">
              <badge-button
                :quantity="cartsCount"
                iconClass="bi-cart-fill"
                redirectURL="/my-cart"/>
            </div>
          </nav-tab>
        </div>
      </div>

      <!-- To avoid display loading spinner and showing `No more service sales found.` message -->
      <div v-else-if="loading">
        <item-not-found message="Loading service sale." />
      </div>

      <!-- Service Sales not found -->
      <div v-else>
        <item-not-found message="No more service sales found." />
      </div>
    </pull-to-refresh>
    <product-listing-filter-modal
      :title="'Service Sales Options'"
      :groupBy="groupBy"
      @update:groupBy="updateGroupBySelection"/>
  </div>
</template>

<script>
import {
  defineAsyncComponent,
  markRaw
} from "vue";
import store from "@/store";
import { mapActions, mapGetters, mapMutations } from "vuex";
import {
  formatProcessingTime,
  getPromotionHeaderAlt
} from "@/common/helpers/ProductHelper";
import { getFormattedNumber } from "@/common/helpers/PriceHelper.js";
import { getToken } from "@/services/jwtService";
import BadgeButton from "@/components/button/BadgeButton.vue";
import IconContainer from "@/components/IconContainer.vue";
import ListHeader from "@/components/ListHeader.vue";
import ExpandableList from "@/components/ExpandableList.vue";
import ExpandableListHeader from "@/components/ExpandableListHeader.vue";
import ExpandableListItem from "@/components/ExpandableListItem.vue";
import NavTab from "@/components/tab/NavTab.vue";
import IconButton from "@/components/button/IconButton.vue";
import ProductListingFilterModal, { Modal } from "../components/ProductListingFilterModal.vue";
import Tab from "@/components/tab/Tab.vue";
import ItemNotFound from "@/components/ItemNotFound.vue";
import PullToRefresh from "@/components/PullToRefresh";
import { openModal, closeModal } from "@/store/modal";

export default {
  name: "ServiceSales",
  components: {
    BadgeButton,
    IconContainer,
    ListHeader,
    ExpandableList,
    ExpandableListHeader,
    ExpandableListItem,
    NavTab,
    IconButton,
    ProductListingFilterModal,
    Tab,
    ItemNotFound,
    PullToRefresh,
  },
  data() {
    return {
      groupBy: "section", // value is either section/category. default as section.
      selectedTabID: null,
      selectedServiceSaleID: null,
    };
  },
  computed: {
    isLoggedIn() {
      return getToken() != null;
    },
    populatedServiceSales() {
      // defined listing - list of service sales section data
      const list = this.serviceSales?.reduce((accumulated, serviceSale) => {
        const sectionId = serviceSale?.productSection?.id || 0;
        const categoryId = serviceSale?.productCategory?.id || 0;
        const exists = this.groupBy === "section" ?
            accumulated.find((data) => data.sectionID == sectionId) : // group by section
            accumulated.find((data) => data.categoryID == categoryId); // group by category

        if (exists === undefined) {
          accumulated.push({
            sectionID: sectionId || 0,
            categoryID: categoryId || 0,
            sectionName: serviceSale?.productSection?.name || 'Others',
            categoryName: serviceSale?.productCategory?.name || 'Others',
            serviceSales: [],
          });
        };

        return accumulated;
      }, []);

      // populate service sale section data serviceSales list
      if (list !== undefined && list.length > 0) {
        list.map((data) => {
          data.serviceSales = this.serviceSales?.
            filter((serviceSale) => {
              const sectionId = serviceSale?.productSection?.id || 0;
              const categoryId = serviceSale?.productCategory?.id || 0;

              return this.groupBy === "section" ?
                sectionId === data.sectionID : // group by section
                categoryId === data.categoryID; // group by category
            })
            .map((serviceSale) => {
              return {
                id: serviceSale.id,
                name: serviceSale.name,
                type: serviceSale.type,
                description: serviceSale.description,
                processingTime: serviceSale.service.processingTime,
                formatProcessingTime: serviceSale.service.formatProcessingTime,
                imageURL: serviceSale.image,
                section: serviceSale.productSection?.name,
                category: serviceSale.productCategory?.name,
                promotion: serviceSale.promotion,
                salePrice: serviceSale.salePrice,
                originalPrice: serviceSale.originalPrice,
                promotionPrice: serviceSale.service.promotionPrice,
                promotionStartDate: serviceSale.service.startDate,
                promotionEndDate: serviceSale.service.endDate,
              };
            });
        });
      }

      return list;
    },
    tabList() {
      if (this.populatedServiceSales) {
        return this.populatedServiceSales
          .map((serviceSale) => {
            return this.groupBy === "section" ?
              // group by section
              {
                id: serviceSale.sectionID,
                name: `${serviceSale.sectionName} (${serviceSale.serviceSales.length})`,
              } :
              // group by category
              {
                id: serviceSale.categoryID,
                name: `${serviceSale.categoryName} (${serviceSale.serviceSales.length})`,
              };
          })
          .sort((a, b) => {
            // Make the others always at the end
            if (a.id === 0) {
              return 1;
            }
            if (b.id === 0) {
              return -1;
            }

            // Sort as normal
            return a.id > b.id ? 1 : -1;
          });
      }

      return [];
    },
    selectedServiceSaletDetails() {
      return this.setSelectedServiceSale(this.selectedServiceSaleID);
    },
    buttonEnabled() {
      return Boolean(this.selectedServiceSaleID);
    },
    ...mapGetters({
      serviceSales: "serviceSales",
      loading: "showLoading",
      carts: "carts",
      cartsCount: "cartsCount",
    }),
  },
  methods: {
    showGroupByModal() {
      var myModal = new Modal(document.getElementById("product-listing-filter-modal-id")); // use <product-listing-filter-modal /> default id
      myModal.show();
    },
    updateGroupBySelection(value) {
      this.groupBy = value;
      this.selectedTabID = null; // reset
    },
    async fetchData() {
      const actions = [];

      // If serviceSales is not fetched
      if (this.serviceSales === null) {
        actions.push("getServiceSales");
      }

      // If user is logging and carts is empty
      if (this.isLoggedIn) {
        actions.push("getCarts");
      }

      try {
        actions.forEach((action) => store.dispatch(action));
      } catch (error) {
        console.log(error);
      }
    },
    setSelectedServiceSales(id) {
      let serviceSaleList = this.populatedServiceSales
        .map((serviceSale) => serviceSale.services)
        .flat(1);

      return serviceSaleList.find((serviceSale) => serviceSale.id === parseInt(id));
    },
    viewItemDetail(data) {
      openModal(
        {
          id: "expandableListItemDetailModal",
        },
        markRaw(defineAsyncComponent(() => import("@/components/ExpandableListItemDetail.vue"))),
        {
          image: data.imageURL,
          processingTime: data.formatProcessingTime,
          name: data.name,
          description: data.description,
          originalPrice: data.originalPrice,
          salePrice: data.salePrice,
          section: data.section,
          category: data.category,
          promotion: data.promotion,
        },
        {},
        [
          {
            text: 'Add to cart',
            class: 'scss-blue-button',
            clickFn: async () => {
              if (!this.isLoggedIn) {
                this.$router.push("/login");
                closeModal();
                return;
              }

              store.commit("showLoading", true);
              await store.dispatch('postCart', {
                productId: data.id
              });
              closeModal();
              store.commit("showLoading", false);
            }
          },
          {
            text: 'Close',
            clickFn: () => {
              closeModal();
            }
          }
        ]
      );
    },
    formatProcessingTime,
    getPromotionHeaderAlt,
    getFormattedNumber,
    async addToCart(productId) {
      if (!this.isLoggedIn) {
        this.$router.push("/login");
      }

      store.commit("showLoading", true);
      await store.dispatch('postCart', {
        productId
      });
      store.commit("showLoading", false);
    },
    ...mapActions([
      "getServiceSales",
      "getCarts",
    ]),
    ...mapMutations(["showLoading"]),
  },
  watch: {
    populatedServiceSales: {
      handler(newValue) {
        if (newValue !== undefined) {
          this.showLoading(false);
        } else {
          this.showLoading(true);
        }
      },
      immediate: true
    },
  },
  created() {
    this.fetchData();
  },
};
</script>

<style lang="scss" scoped>
.style-checkbox {
  font-size: 20px;
}
.style-action-container {
  margin: 0 auto 0 auto;
  padding-top: 5px;
  max-width: 510px;
  border: 1px solid $border-light-grey;
  background-color: white;
  z-index: 5;
}
.style-content {
  width: 90%;
  margin: 0 auto 0 auto;
  margin-bottom: 70px;
}
.style-action-container {
  background-color: white;
  border: 1px solid $border-light-grey;
  bottom: 0;
  margin: 0 auto 0 auto;
  max-width: 510px;
  padding-top: 5px;
  z-index: 5;
}
</style>
